import React from 'react'
import {
  FormatBold, FormatItalic, FormatStrikethrough, FormatListBulleted, FormatColorText, LocalOfferOutlined,
  Link as LinkIcon, LinkOff, OpenInNew, FormatClear, DeleteOutline, FormatAlignLeft, FormatAlignCenter, FormatAlignRight,
} from '@mui/icons-material'
import { normalizeUrl, parseParagraph, PARAGRAPH_MAX_CODES, PARAGRAPH_MAX_LINKS, PROMO_CODE, Style } from './paragraphFormat'
import { ACCENT, codeMarkupHtml, colorIsAccent, esc, insertAtRange, lineOf, linesInRange, serialize, setLineAlign, toHtml } from './paragraphDom'
import s from './ParagraphEditor.module.css'

/*
 * The campaign paragraph as an editor: bold, italic, struck, the accent
 * colour, a bulleted list, line alignment, links and a promo code —
 * nothing else. The value
 * in and out is the markup of paragraphFormat.ts; the browser's own editing
 * (execCommand) does the typing, and what it leaves in the DOM is read back
 * into that markup on every change.
 *
 * ⌘/Ctrl+B, I, ⇧X and K; "- " at the start of a line starts a list. Pasting
 * keeps the text only; underline (⌘U) stays off — underlined text reads as a
 * link. Styles inside a link or a promo code are not kept.
 */

const codeHtml = (code: string) => codeMarkupHtml(code, s.code)

/* ── the editor ─────────────────────────────────────────────────────── */

type Bar =
  | { kind: 'link'; range: Range | null; anchor?: HTMLAnchorElement; value: string; error?: string }
  | { kind: 'code'; range: Range | null; span?: HTMLElement; value: string; error?: string }

type Active = Record<Style | 'list', boolean> & { align: 'left' | 'center' | 'right' }
const NONE: Active = { b: false, i: false, s: false, a: false, list: false, align: 'left' }

export function ParagraphEditor({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const ref = React.useRef<HTMLDivElement>(null)
  const emitted = React.useRef<string | null>(null)
  // what was clicked last — a promo code takes no caret, so its line is found by it
  const clicked = React.useRef<Node | null>(null)
  const [bar, setBar] = React.useState<Bar | null>(null)
  const [active, setActive] = React.useState<Active>(NONE)

  // the value from outside (first render, a reset) — not the one just typed
  React.useEffect(() => {
    if (ref.current && value !== emitted.current) {
      ref.current.innerHTML = toHtml(parseParagraph(value), s.code)
      emitted.current = value
    }
  }, [value])

  const inEditor = (n: Node | null) => !!n && !!ref.current?.contains(n)
  const selRange = () => {
    const sel = window.getSelection()
    return sel && sel.rangeCount && inEditor(sel.anchorNode) ? sel.getRangeAt(0) : null
  }

  // which buttons light up for where the caret is
  React.useEffect(() => {
    const update = () => {
      const sel = window.getSelection()
      if (!sel || !sel.rangeCount || !ref.current?.contains(sel.anchorNode)) return
      const r = sel.getRangeAt(0)
      const el = (r.startContainer.nodeType === Node.TEXT_NODE ? r.startContainer.parentElement : r.startContainer) as HTMLElement | null
      const css = el ? getComputedStyle(el) : null
      const color = css?.color || ''
      const textAlign = css?.textAlign || ''
      setActive({
        align: textAlign === 'center' ? 'center' : textAlign === 'right' || textAlign === 'end' ? 'right' : 'left',
        b: document.queryCommandState('bold'),
        i: document.queryCommandState('italic'),
        s: document.queryCommandState('strikeThrough'),
        a: colorIsAccent(color),
        list: !!el?.closest('li'),
      })
    }
    document.addEventListener('selectionchange', update)
    return () => document.removeEventListener('selectionchange', update)
  }, [])

  function emit() {
    if (!ref.current) return
    const v = serialize(ref.current)
    // a stray <br> would hide the placeholder
    if (!v && ref.current.innerHTML && !ref.current.querySelector('li')) ref.current.innerHTML = ''
    emitted.current = v
    onChange(v)
  }

  function exec(command: string, arg?: string) {
    if (!ref.current) return
    if (!selRange()) ref.current.focus()
    document.execCommand('styleWithCSS', false, 'false')
    document.execCommand('defaultParagraphSeparator', false, 'div')
    document.execCommand(command, false, arg)
    emit()
  }

  // whole lines, not the browser's justify — that splits a line around a promo code
  function align(dir: 'left' | 'center' | 'right') {
    const root = ref.current
    if (!root) return
    const r = selRange()
    const lines = r ? linesInRange(root, r) : []
    if (!lines.length && clicked.current && inEditor(clicked.current)) {
      const line = lineOf(root, clicked.current)
      if (line) lines.push(line)
    }
    if (!lines.length) return
    setLineAlign(lines, dir)
    setActive((a) => ({ ...a, align: dir }))
    emit()
  }

  function toggleAccent() {
    if (active.a) exec('foreColor', getComputedStyle(ref.current as HTMLElement).color)
    else exec('foreColor', ACCENT)
  }

  const count = (sel: string) => ref.current?.querySelectorAll(sel).length || 0
  const limitError = (kind: Bar['kind']) => {
    if (kind === 'link' && count('a') >= PARAGRAPH_MAX_LINKS) return `Up to ${PARAGRAPH_MAX_LINKS} links in a paragraph`
    if (kind === 'code' && count('[data-code]') >= PARAGRAPH_MAX_CODES) return 'One promo code per paragraph'
    return undefined
  }

  function openBar(kind: Bar['kind']) {
    const r = selRange()
    const range = r ? r.cloneRange() : null
    const start = range ? (range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : range.startContainer as HTMLElement) : null
    if (kind === 'link') {
      const anchor = start?.closest('a') as HTMLAnchorElement | null
      if (anchor && inEditor(anchor)) { setBar({ kind, range, anchor, value: anchor.getAttribute('href') || '' }); return }
      setBar({ kind, range, value: '', error: limitError(kind) })
    } else {
      setBar({ kind, range, value: range && !range.collapsed ? range.toString().replace(/\s+/g, '') : '', error: limitError(kind) })
    }
  }

  function restore(range: Range | null) {
    ref.current?.focus()
    const sel = window.getSelection()
    if (range && sel) { sel.removeAllRanges(); sel.addRange(range) }
  }

  function apply() {
    if (!bar || !ref.current) return
    if (bar.kind === 'link') {
      const url = normalizeUrl(bar.value)
      if (!url) { setBar({ ...bar, error: 'Enter a web address — https://…' }); return }
      if (bar.anchor) bar.anchor.setAttribute('href', url)
      else {
        if (limitError('link')) return
        restore(bar.range)
        if (bar.range && !bar.range.collapsed) document.execCommand('createLink', false, url)
        else document.execCommand('insertHTML', false, `<a href="${esc(url)}">${esc(url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, ''))}</a>&nbsp;`)
      }
    } else {
      const code = bar.value.trim()
      if (!PROMO_CODE.test(code)) { setBar({ ...bar, error: 'Letters, digits, - and _ only, up to 20' }); return }
      if (bar.span) { bar.span.dataset.code = code; bar.span.textContent = code } else {
        if (limitError('code')) return
        restore(bar.range)
        // put in by hand: insertHTML drops a code typed at a line's end out of that line
        const t = document.createElement('template')
        t.innerHTML = codeHtml(code)
        insertAtRange(ref.current, selRange(), [t.content.firstChild as Node, document.createTextNode('\u00a0')])
      }
    }
    setBar(null)
    emit()
  }

  function remove() {
    if (!bar) return
    if (bar.kind === 'link' && bar.anchor) bar.anchor.replaceWith(document.createTextNode(bar.anchor.textContent || ''))
    if (bar.kind === 'code' && bar.span) bar.span.remove()
    ref.current?.normalize()
    setBar(null)
    emit()
  }

  function onKeyDown(e: React.KeyboardEvent) {
    const mod = e.metaKey || e.ctrlKey
    const key = e.key.toLowerCase()
    if (mod && key === 'k') { e.preventDefault(); openBar('link'); return }
    if (mod && !e.shiftKey && key === 'b') { e.preventDefault(); exec('bold'); return }
    if (mod && !e.shiftKey && key === 'i') { e.preventDefault(); exec('italic'); return }
    if (mod && e.shiftKey && key === 'x') { e.preventDefault(); exec('strikeThrough'); return }
    if (mod && key === 'u') { e.preventDefault(); return }
    const r = selRange()
    const startEl = r ? (r.startContainer.nodeType === Node.TEXT_NODE ? r.startContainer.parentElement : r.startContainer as HTMLElement) : null
    const inItem = !!startEl?.closest('li')
    if (e.key === 'Enter') {
      // the browser makes the next line (keeping its alignment) or the next list item, and leaves a list on an
      // empty item; a line break inside an item would not survive, so there Shift+Enter is a new item too
      document.execCommand('defaultParagraphSeparator', false, 'div')
      if (inItem && e.shiftKey) { e.preventDefault(); exec('insertParagraph') }
      return
    }
    // "- " at the start of a line starts a list
    if (e.key === ' ' && r && r.collapsed && !inItem && r.startContainer.nodeType === Node.TEXT_NODE && r.startOffset === 1) {
      const text = r.startContainer as Text
      const prev = text.previousSibling
      if (text.data[0] === '-' && (!prev || ['BR', 'UL', 'DIV'].includes((prev as HTMLElement).tagName))) {
        e.preventDefault()
        text.deleteData(0, 1)
        exec('insertUnorderedList')
      }
    }
  }

  function onPaste(e: React.ClipboardEvent) {
    e.preventDefault()
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'))
  }

  function onClick(e: React.MouseEvent) {
    const target = e.target as HTMLElement
    clicked.current = target
    const span = target.closest('[data-code]') as HTMLElement | null
    if (span && inEditor(span)) { setBar({ kind: 'code', range: null, span, value: span.dataset.code || '' }); return }
    const a = target.closest('a')
    if (a && inEditor(a)) { e.preventDefault(); setBar({ kind: 'link', range: null, anchor: a, value: a.getAttribute('href') || '' }) }
  }

  const tool = (on: boolean, title: string, onClick: () => void, icon: React.ReactNode, disabled?: boolean) => (
    <button
      type="button"
      className={`${s.tool} ${on ? s.toolOn : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={on}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >{icon}</button>
  )
  const editing = bar && ((bar.kind === 'link' && bar.anchor) || (bar.kind === 'code' && bar.span))
  const openHref = bar?.kind === 'link' && bar.anchor ? normalizeUrl(bar.value) : null

  return (
    <div className={s.wrap}>
      <div className={s.tools}>
        {tool(active.b, 'Bold (⌘/Ctrl+B)', () => exec('bold'), <FormatBold sx={{ fontSize: 18 }} />)}
        {tool(active.i, 'Italic (⌘/Ctrl+I)', () => exec('italic'), <FormatItalic sx={{ fontSize: 18 }} />)}
        {tool(active.s, 'Strikethrough (⌘/Ctrl+Shift+X)', () => exec('strikeThrough'), <FormatStrikethrough sx={{ fontSize: 18 }} />)}
        <span className={s.sep} />
        {tool(active.list, 'Bulleted list — or type "- "', () => exec('insertUnorderedList'), <FormatListBulleted sx={{ fontSize: 18 }} />)}
        <span className={s.sep} />
        {tool(!active.list && active.align === 'left', 'Align left', () => align('left'), <FormatAlignLeft sx={{ fontSize: 18 }} />, active.list)}
        {tool(!active.list && active.align === 'center', 'Align centre', () => align('center'), <FormatAlignCenter sx={{ fontSize: 18 }} />, active.list)}
        {tool(!active.list && active.align === 'right', 'Align right', () => align('right'), <FormatAlignRight sx={{ fontSize: 18 }} />, active.list)}
        <span className={s.sep} />
        {tool(active.a, 'Accent colour', toggleAccent, <FormatColorText sx={{ fontSize: 18, color: active.a ? undefined : ACCENT }} />)}
        {tool(false, 'Promo code — users get a Copy button', () => openBar('code'), <LocalOfferOutlined sx={{ fontSize: 17 }} />)}
        {tool(false, 'Link (⌘/Ctrl+K)', () => openBar('link'), <LinkIcon sx={{ fontSize: 18 }} />)}
        <span className={s.sep} />
        {tool(false, 'Clear formatting of the selection', () => exec('removeFormat'), <FormatClear sx={{ fontSize: 18 }} />)}
      </div>
      <div
        ref={ref}
        className={s.editor}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline
        data-placeholder={placeholder}
        onInput={emit}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onClick={onClick}
      />
      {bar && (
        <div className={s.bar}>
          {bar.kind === 'link' ? <LinkIcon sx={{ fontSize: 16 }} className={s.barIcon} /> : <LocalOfferOutlined sx={{ fontSize: 15 }} className={s.barIcon} />}
          <input
            className={`${s.url} ${bar.kind === 'code' ? s.codeInput : ''}`}
            autoFocus
            placeholder={bar.kind === 'link' ? 'https://…' : 'SPRING20'}
            maxLength={bar.kind === 'code' ? 20 : undefined}
            value={bar.value}
            onChange={(e) => setBar({ ...bar, value: bar.kind === 'code' ? e.target.value.replace(/\s/g, '') : e.target.value, error: undefined })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); apply() }
              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setBar(null) }
            }}
          />
          {openHref && <a className={s.barBtn} href={openHref} target="_blank" rel="noopener noreferrer" title="Open"><OpenInNew sx={{ fontSize: 15 }} /></a>}
          {editing && (
            <button type="button" className={s.barBtn} onClick={remove} title={bar.kind === 'link' ? 'Remove link' : 'Remove promo code'}>
              {bar.kind === 'link' ? <LinkOff sx={{ fontSize: 15 }} /> : <DeleteOutline sx={{ fontSize: 15 }} />}
            </button>
          )}
          <button type="button" className={s.barText} onClick={() => setBar(null)}>Cancel</button>
          <button type="button" className={s.barApply} onClick={apply} disabled={!editing && !!limitError(bar.kind)}>{editing ? 'Save' : 'Add'}</button>
          {bar.error && <span className={s.barErr}>{bar.error}</span>}
        </div>
      )}
    </div>
  )
}
